"use client";

import * as React from "react";
import type { Table } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button, cn } from "@aio/platform-ui";

/**
 * Default page size options surfaced in the size dropdown. Callers can
 * override per-table via {@link PaginationProps.pageSizeOptions}.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Props for {@link Pagination}.
 */
export interface PaginationProps<TData> {
  /** Configured TanStack Table instance. */
  table: Table<TData>;
  /** Override the page size options. Defaults to 10/25/50/100. */
  pageSizeOptions?: readonly number[];
  /**
   * Override the rendered "{n} rows" total label. Caller receives the live
   * row count from `table.getFilteredRowModel().rows.length`.
   */
  formatTotal?: (rowCount: number) => string;
  /** Extra classes for the wrapping element. */
  className?: string;
}

/**
 * Render the default "{n} rows" copy. Uses `Intl.NumberFormat` so large row
 * counts pick up locale-aware grouping. We don't reach into platform-i18n
 * here — the engine receives a `locale` separately when the wider data
 * table is composed; this footer falls back to the document locale.
 */
function defaultTotalLabel(count: number): string {
  // jsdom's default locale is "en-US"; production callers can swap via
  // `formatTotal` if a different locale is needed.
  const formatted = new Intl.NumberFormat(undefined).format(count);
  return `${formatted} ${count === 1 ? "row" : "rows"}`;
}

/**
 * Pagination footer for a TanStack-Table-backed datagrid.
 *
 * Layout (from left to right):
 *   [ "{n} rows" total ] [ "Page X of Y" status ] [ page-size select ] [ Prev ] [ Next ]
 *
 * Behaviour:
 * - The page-size select uses a native `<select>` styled with platform-ui
 *   tokens. Going through a `Select` primitive would give us a richer
 *   popover but the platform doesn't ship one yet — `<select>` is the
 *   correct fallback because it's accessible by default, mobile-friendly,
 *   and keyboard-driven. Swapping to a Radix Select later is mechanical.
 * - Prev/Next call TanStack's `previousPage()` / `nextPage()` and respect
 *   the engine's own page-bounds via `getCanPreviousPage()` /
 *   `getCanNextPage()`.
 * - Page count uses `Math.max(1, getPageCount())` so empty tables read as
 *   "Page 1 of 1" rather than "Page 1 of 0".
 */
export function Pagination<TData>({
  table,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  formatTotal,
  className,
}: PaginationProps<TData>): React.JSX.Element {
  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const pageCount = Math.max(1, table.getPageCount());
  const totalRows = table.getFilteredRowModel().rows.length;
  const totalLabel = (formatTotal ?? defaultTotalLabel)(totalRows);

  const onPageSizeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = Number(event.target.value);
      if (Number.isFinite(next) && next > 0) {
        table.setPageSize(next);
      }
    },
    [table],
  );

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t border-border bg-bg-elevated px-3 py-2",
        className,
      )}
      data-testid="pagination"
    >
      <div className="flex items-center gap-4 text-sm text-fg-muted">
        <span data-testid="pagination-total">{totalLabel}</span>
        <span data-testid="pagination-status" aria-live="polite">
          Page {pageIndex + 1} of {pageCount}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          <span>Rows per page</span>
          <select
            value={pageSize}
            onChange={onPageSizeChange}
            className={cn(
              "h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg",
              "transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            data-testid="pagination-page-size"
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
            data-testid="pagination-prev"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
            data-testid="pagination-next"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </nav>
  );
}
